import { ServerMessagePayload } from '@common/api';
import { SCENE_DEFINITIONS } from '@common/scenes';
import { Container, Graphics, RenderTexture, Sprite } from 'pixi.js';

import { LightSourceInfo } from '../services/LightingService';
import { ClientGlobalSystem } from './ClientBase';

/** Size of lighting tiles in pixels (controls rendering resolution) */
const LIGHT_TILE_SIZE = 16;

/** Extra tiles to render beyond viewport to prevent edge pop-in */
const VIEWPORT_MARGIN_TILES = 2;

/** Z-index for lighting layer (above sprites, below UI) */
const LIGHTING_Z_INDEX = 900000;

/** Maximum light level (full brightness) */
const MAX_LIGHT_LEVEL = 15;

/**
 * Renders the darkness overlay for the day/night cycle.
 *
 * Uses a low-resolution RenderTexture scaled up with linear interpolation
 * to create smooth shadows efficiently. The texture is rendered with multiply
 * blend mode to darken the scene based on calculated light levels.
 */
export class LightingRenderSystem extends ClientGlobalSystem {
  private lightingContainer: Container | null = null;
  private renderTexture: RenderTexture | null = null;
  private renderSprite: Sprite | null = null;
  private drawingGraphics: Graphics | null = null;

  run(): ServerMessagePayload[] {
    if (!this.shouldRenderLighting()) {
      return [];
    }

    const { stage, app } = this.clientServices.render;
    if (!stage || !app) {
      return [];
    }

    this.initializeContainer(stage);
    this.initializeDrawingGraphics();
    this.updateAmbientLight();

    const viewport = this.calculateViewport(app.screen.width, app.screen.height);
    if (!viewport) {
      return [];
    }

    this.ensureRenderTexture(viewport.widthTiles, viewport.heightTiles);
    if (!this.renderSprite) {
      return [];
    }

    this.positionRenderSprite(viewport.startTileX, viewport.startTileY);

    const lightSources = this.collectLightSources();
    this.renderLightingToTexture(viewport, lightSources);

    app.renderer.render({
      container: this.drawingGraphics!,
      target: this.renderTexture!,
    });

    return [];
  }

  /**
   * Check if lighting should be rendered this frame.
   */
  private shouldRenderLighting(): boolean {
    const { debug, lighting } = this.clientServices;

    if (!debug.state.showLighting || !lighting.enabled) {
      if (this.lightingContainer) {
        this.lightingContainer.visible = false;
      }
      return false;
    }
    return true;
  }

  /**
   * Initialize the lighting container on the stage.
   */
  private initializeContainer(stage: Container): void {
    if (!this.lightingContainer) {
      this.lightingContainer = new Container();
      this.lightingContainer.label = 'LightingContainer';
      this.lightingContainer.zIndex = LIGHTING_Z_INDEX;
      stage.addChild(this.lightingContainer);
    }
    this.lightingContainer.visible = true;
  }

  /**
   * Initialize the off-screen Graphics object used for drawing to texture.
   */
  private initializeDrawingGraphics(): void {
    if (!this.drawingGraphics) {
      this.drawingGraphics = new Graphics();
    }
  }

  /**
   * Sync ambient light level from game time or scene override.
   */
  private updateAmbientLight(): void {
    const { lighting, localPlayer } = this.clientServices;
    const { gameTime } = this.services;

    // Check for scene-specific ambient light override
    const entityId = localPlayer.entityId;
    if (entityId !== undefined) {
      const transform = this.getWorld().getComponent(entityId, 'Transform');
      if (transform?.sceneId) {
        const sceneDef = SCENE_DEFINITIONS[transform.sceneId];
        if (sceneDef.ambientLightOverride !== undefined) {
          // Clamp to valid range and convert from 0-15 scale to 0.0-1.0
          const clampedLevel = Math.max(
            0,
            Math.min(MAX_LIGHT_LEVEL, sceneDef.ambientLightOverride),
          );
          lighting.ambientLight = clampedLevel / MAX_LIGHT_LEVEL;
          return;
        }
      }
    }

    // Default: use time-based ambient light
    lighting.ambientLight = gameTime.getAmbientLightFloat();
  }

  /**
   * Calculate the visible viewport in tile coordinates.
   * Uses the camera position (not player position) since the camera may be
   * clamped to map boundaries and offset from the player.
   * Returns null if camera position is unavailable.
   */
  private calculateViewport(
    screenWidth: number,
    screenHeight: number,
  ): {
    startTileX: number;
    startTileY: number;
    widthTiles: number;
    heightTiles: number;
  } | null {
    const cameraService = this.clientServices.camera;
    const cameraEntityId = cameraService.cameraEntityId;
    if (cameraEntityId === undefined) {
      return null;
    }

    const world = this.getWorld();
    const cameraTransform = world.getComponent(cameraEntityId, 'Transform');

    if (!cameraTransform) {
      return null;
    }

    const zoom = cameraService.zoom;
    const viewportWidth = screenWidth / zoom;
    const viewportHeight = screenHeight / zoom;
    const viewportX = cameraTransform.x - viewportWidth / 2;
    const viewportY = cameraTransform.y - viewportHeight / 2;

    const startTileX = Math.floor(viewportX / LIGHT_TILE_SIZE) - VIEWPORT_MARGIN_TILES;
    const startTileY = Math.floor(viewportY / LIGHT_TILE_SIZE) - VIEWPORT_MARGIN_TILES;
    const widthTiles = Math.ceil(viewportWidth / LIGHT_TILE_SIZE) + VIEWPORT_MARGIN_TILES * 2;
    const heightTiles = Math.ceil(viewportHeight / LIGHT_TILE_SIZE) + VIEWPORT_MARGIN_TILES * 2;

    return { startTileX, startTileY, widthTiles, heightTiles };
  }

  /**
   * Ensure the RenderTexture exists and is the correct size.
   * Recreates it if dimensions have changed.
   */
  private ensureRenderTexture(widthTiles: number, heightTiles: number): void {
    if (
      !this.renderTexture ||
      this.renderTexture.width !== widthTiles ||
      this.renderTexture.height !== heightTiles
    ) {
      if (this.renderTexture) this.renderTexture.destroy(true);
      if (this.renderSprite) this.renderSprite.destroy();

      this.renderTexture = RenderTexture.create({
        width: widthTiles,
        height: heightTiles,
        scaleMode: 'linear',
      });

      this.renderSprite = new Sprite(this.renderTexture);
      this.renderSprite.blendMode = 'multiply';
      this.renderSprite.scale.set(LIGHT_TILE_SIZE);

      this.lightingContainer!.addChild(this.renderSprite);
    }
  }

  /**
   * Position the render sprite to align with world coordinates.
   */
  private positionRenderSprite(startTileX: number, startTileY: number): void {
    this.renderSprite!.position.set(startTileX * LIGHT_TILE_SIZE, startTileY * LIGHT_TILE_SIZE);
  }

  /**
   * Collect all active light sources from entities in the world.
   */
  private collectLightSources(): LightSourceInfo[] {
    const { lighting, localPlayer } = this.clientServices;
    const world = this.getWorld();
    const localEntityId = localPlayer.entityId;
    const lightSources: LightSourceInfo[] = [];

    const entities = world.query(['LightSource', 'Transform']);
    for (const entityId of entities) {
      const lightSource = world.getComponent(entityId, 'LightSource')!;
      const transform = world.getComponent(entityId, 'Transform')!;

      // Skip local-only light sources that don't belong to this player
      if (lightSource.localOnly && entityId !== localEntityId) {
        continue;
      }

      lightSources.push(lighting.componentToInfo(lightSource, transform.x, transform.y));
    }

    return lightSources;
  }

  /**
   * Render all lighting tiles to the internal Graphics object.
   */
  private renderLightingToTexture(
    viewport: { startTileX: number; startTileY: number; widthTiles: number; heightTiles: number },
    lightSources: LightSourceInfo[],
  ): void {
    const { lighting } = this.clientServices;

    this.drawingGraphics!.clear();

    for (let y = 0; y < viewport.heightTiles; y++) {
      for (let x = 0; x < viewport.widthTiles; x++) {
        const tileX = viewport.startTileX + x;
        const tileY = viewport.startTileY + y;

        const worldX = tileX * LIGHT_TILE_SIZE + LIGHT_TILE_SIZE / 2;
        const worldY = tileY * LIGHT_TILE_SIZE + LIGHT_TILE_SIZE / 2;

        const result = lighting.calculateLightAtPosition(worldX, worldY, lightSources);
        const color = this.calculateTileColor(result.level, result.color, result.colorAlpha);

        // PixiJS v8 API: rect().fill() instead of beginFill/drawRect/endFill
        this.drawingGraphics!.rect(x, y, 1, 1).fill(color);
      }
    }
  }

  /**
   * Calculate the final color for a lighting tile.
   *
   * @param level - Light level (0-15)
   * @param lightColor - Base light color (hex)
   * @param colorAlpha - Color intensity (0 = white, 1 = full color)
   * @returns Final color for multiply blend mode
   */
  private calculateTileColor(level: number, lightColor: number, colorAlpha: number): number {
    const brightness = level / MAX_LIGHT_LEVEL;

    // Extract RGB from light color
    const lightR = (lightColor >> 16) & 0xff;
    const lightG = (lightColor >> 8) & 0xff;
    const lightB = lightColor & 0xff;

    // Blend between white (255) and the light color based on colorAlpha
    // colorAlpha = 0 -> pure white, colorAlpha = 1 -> full light color
    const blendedR = Math.round(255 + (lightR - 255) * colorAlpha);
    const blendedG = Math.round(255 + (lightG - 255) * colorAlpha);
    const blendedB = Math.round(255 + (lightB - 255) * colorAlpha);

    // Scale by brightness
    const r = Math.round(blendedR * brightness);
    const g = Math.round(blendedG * brightness);
    const b = Math.round(blendedB * brightness);

    return (r << 16) | (g << 8) | b;
  }
}
